import React from 'react';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { useLazyGetSuggestResultQuery, useSuggestResultQueryState } from 'store/api/activitiesApi';
import { SuggestionSearchResponse } from 'component/common/TextSearch/suggestionUtils';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap, selectGeoFromBootstrap } from 'store/slice/bootstrap';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesFieldsV1,
    ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesFieldsV1,
    ActivitiesOverlayTextSearchInputChangeEventActivitiesFieldsV1,
    ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesManifestV1,
    ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesManifestV1,
    ActivitiesOverlayTextSearchInputChangeEventActivitiesManifestV1,
    ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { Input, SearchField } from '@drone-js/core';
import { Geo } from 'core/type/BootstrapConfig.types';
import { useLocation } from 'react-router-dom';
import { getCityIdQueryParams } from 'gql/service/graph/activityService';

const CITY_TERM = 'city'; // default searchText for popular city suggestion

interface OverlayTextSearchInputProps {
    isMockData: boolean;
    isTextSearchOverlayVisible: boolean;
    searchText: string;
    setSearchText: React.Dispatch<React.SetStateAction<string>>;
    isGlobalKeywordSearch?: boolean;
    isModifyEnterKeyPressForSearchExpOn: boolean;
    onKeywordResultSelect?: (keyword: string, geo: Geo) => void;
    onGlobalKeywordResultSelect?: (keyword: string) => void;
    isDisabledGlobalKeywordSearch?: boolean;
}

const isTextSearchDataAvailableInCache = (response: SuggestionSearchResponse) => {
    return response && response.isSuccess && response.data && response.data.suggestionList && response.data.suggestionList.length > 0;
};

export const OverlayTextSearchInput: React.FC<OverlayTextSearchInputProps> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const geo = useSelector(selectGeoFromBootstrap);
    const isSearchBoxFeatureGroup = useSelector(selectFeatureBehaviorFromBootstrap).SearchBoxFeatureGroup;
    const isMockData = props.isMockData;
    const prevSuggestionResponse = useSuggestResultQueryState(props.searchText);
    const [triggerGetSuggestResultQuery] = useLazyGetSuggestResultQuery();
    const textSearchInput = React.useRef<HTMLInputElement>(null);
    const location = useLocation();

    const callService = React.useMemo(
        () =>
            debounce((value: string) => {
                if (value && value.length >= 1) {
                    if (isMockData) return;
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    triggerGetSuggestResultQuery(value);
                    trackElementEvent<ActivitiesOverlayTextSearchInputChangeEventActivitiesFieldsV1>(
                        ActivitiesOverlayTextSearchInputChangeEventActivitiesManifestV1.ids,
                        {},
                        { action_element_value: value },
                    );
                }
            }, 300),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    React.useEffect(() => {
        const hasTextSearchDataInCache = isTextSearchDataAvailableInCache(prevSuggestionResponse as unknown as SuggestionSearchResponse);
        // Do not call text search API when user opens the text search overlay, and we have the results in cache
        if (!isMockData && !hasTextSearchDataInCache) {
            if (isSearchBoxFeatureGroup) {
                if (props.searchText !== '') {
                    callService(props.searchText);
                }
            } else {
                callService(props.searchText === '' ? CITY_TERM : props.searchText);
            }
        }

        debounce(() => {
            if (props.isTextSearchOverlayVisible && textSearchInput && textSearchInput.current) {
                if (!isSearchBoxFeatureGroup) {
                    textSearchInput.current.focus();
                    textSearchInput.current.select();
                }
            }
        }, 100)();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isTextSearchOverlayVisible]);

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.currentTarget.value;
        props.setSearchText(text);

        if (isSearchBoxFeatureGroup) {
            if (text !== '') {
                callService(text);
            }
        } else {
            callService(text || CITY_TERM);
        }
    };

    const onClearTextInputIconClick = () => {
        trackElementEvent<ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesFieldsV2>(ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesManifestV2.ids, {
            search_phrase: props.searchText,
        });
        props.setSearchText('');
        !isSearchBoxFeatureGroup && callService(CITY_TERM);
    };

    const onSearchFieldClear = () => {
        trackElementEvent<ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesFieldsV2>(ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesManifestV2.ids, {
            search_phrase: props.searchText,
        });
        props.setSearchText('');
        !isSearchBoxFeatureGroup && callService(CITY_TERM);
    };

    const placeholderCms = cms[238402];

    const performGeoSearch = (text: string) => {
        trackElementEvent<ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesFieldsV1>(ActivitiesInCitySearchSeeAllResultKeyPressEventActivitiesManifestV1.ids, {
            city_id: geo?.cityId || -1,
        });
        geo && props.onKeywordResultSelect && props.onKeywordResultSelect(text, geo);
    };

    const performGlobalKeywordSearch = (text: string) => {
        trackElementEvent<ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesFieldsV1>(
            ActivitiesGlobalKeywordSearchSeeAllResultKeyPressEventActivitiesManifestV1.ids,
            { search_phrase: text },
        );

        props.onGlobalKeywordResultSelect && props.onGlobalKeywordResultSelect(text);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (props.isGlobalKeywordSearch && e.key === 'Enter' && props.searchText !== '') {
            const cityId = getCityIdQueryParams(location.search);
            if (props.isModifyEnterKeyPressForSearchExpOn) {
                // isDisabledGlobalKeywordSearch is true for city page only, so either its city page or ssr with city id, we will perform geo search
                if ((props.isDisabledGlobalKeywordSearch || cityId) && geo && props.onKeywordResultSelect) {
                    performGeoSearch(props.searchText);
                } else if (props.onGlobalKeywordResultSelect) {
                    performGlobalKeywordSearch(props.searchText);
                }
            } else if (props.onKeywordResultSelect && props.isDisabledGlobalKeywordSearch && geo) {
                performGeoSearch(props.searchText);
            } else {
                performGlobalKeywordSearch(props.searchText);
            }
        }
    };

    return (
        <>
            {!isSearchBoxFeatureGroup ? (
                <Input
                    id="activities-overlay-text-search-input"
                    data-element-name="activities-overlay-text-search-input"
                    data-testid="activities-overlay-text-search-input"
                    clearLabel={cms[264787]}
                    persistentLabel={true}
                    type="text"
                    leadingAsset={{
                        id: 'fill.symbol.search',
                    }}
                    label={placeholderCms}
                    onChange={handleQueryChange}
                    onKeyDown={onKeyDown}
                    onClear={onClearTextInputIconClick}
                    placeholder={placeholderCms}
                    ref={textSearchInput}
                    value={props.searchText}
                    aria-label={placeholderCms}
                    autoFocus={true}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-autocomplete="list"
                    aria-controls="activities-overlay-text-search-result-list"
                    aria-expanded={props.isTextSearchOverlayVisible}
                />
            ) : (
                <SearchField
                    data-element-name="activities-overlay-text-search-input"
                    data-testid="activities-overlay-text-search-input"
                    ref={textSearchInput}
                    variant="input"
                    aria-label={placeholderCms}
                    placeholderText={placeholderCms}
                    inputText={props.searchText}
                    onKeyDown={onKeyDown}
                    onChange={handleQueryChange}
                    leadingAsset={{
                        id: 'fill.symbol.search',
                    }}
                    label={placeholderCms}
                    clearLabel={cms[264787]}
                    onClear={onSearchFieldClear}
                    aria-haspopup="listbox"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls="activities-overlay-text-search-result-list"
                    aria-expanded={props.isTextSearchOverlayVisible}
                />
            )}
        </>
    );
};
OverlayTextSearchInput.displayName = 'OverlayTextSearchInput';
